import { useState, useEffect, useCallback, useRef } from "react";
import { ExternalLink, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getApiBaseUrl, getAuthHeaders } from "@/lib/api";
import { useAuth } from "@/lib/auth";

function getFrontendWebUrl(): string {
  // Set explicitly by docker-compose and by PR preview environments, where the
  // frontend lives on its own hostname and the port arithmetic below is wrong.
  // Listed in build.ts's PUBLIC_ENV_VARS so it folds to "" in the production
  // bundle instead of surviving as an unresolved `process.env` reference.
  if (process.env.BUN_PUBLIC_FRONTEND_URL) return process.env.BUN_PUBLIC_FRONTEND_URL;
  const hostname = window?.location?.hostname;
  if (hostname?.includes("localhost")) {
    // In local docker-compose, frontend port = backoffice port + 1
    const port = parseInt(window.location.port, 10);
    return `http://localhost:${port + 1}`;
  }
  if (hostname?.includes("amino-dev")) return "https://app.amino-dev.akselerasjon.no";
  return "https://app.amino.no";
}

/**
 * Opens the frontend as a specific user in a popup window.
 * Generates a one-time code and opens the frontend with that code.
 */
export function useBrowseAsUser(userId: string | undefined) {
  const [isOpening, setIsOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const popupRef = useRef<Window | null>(null);
  // This button only ever renders on the patient page, which cannot load
  // without an impersonation reason, so the operator has already given one for
  // this exact patient. Reuse it rather than asking a second time.
  const { impersonateReason } = useAuth();

  // Clean up popup ref when it closes
  useEffect(() => {
    const interval = setInterval(() => {
      if (popupRef.current?.closed) {
        popupRef.current = null;
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const openAsUser = useCallback(async () => {
    if (!userId) return;
    setIsOpening(true);
    setError(null);

    try {
      const reason = impersonateReason?.trim();
      if (!reason) throw new Error("Mangler grunn for impersonering");

      const res = await fetch(`${getApiBaseUrl()}/api/authorization/create-impersonation-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ userId, reason }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.error || `HTTP ${res.status}`);
      }
      const data = await res.json() as { code: string; expiresAt: string };
      if (!data?.code) throw new Error("No code returned");

      const url = new URL(getFrontendWebUrl());
      url.searchParams.set("impersonation_code", data.code);

      // Open in a sized popup window (mobile-ish dimensions)
      const width = 430;
      const height = 750;
      const left = window.screenX + window.outerWidth - width - 40;
      const top = window.screenY + 80;
      const features = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,noopener,noreferrer`;

      // Close existing popup if open
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }

      popupRef.current = window.open(url.toString(), "browse-as-user", features);
    } catch (err) {
      console.error("Failed to open browse-as-user:", err);
      setError(err instanceof Error ? err.message : "Failed to create impersonation code");
    } finally {
      setIsOpening(false);
    }
  }, [userId, impersonateReason]);

  return { openAsUser, isOpening, error };
}

export function BrowseAsUserButton({ userId }: { userId: string }) {
  const { openAsUser, isOpening, error } = useBrowseAsUser(userId);

  return (
    <>
      <Button variant="outline" size="sm" onClick={openAsUser} disabled={isOpening}>
        <ExternalLink className="h-4 w-4 mr-1" />
        {isOpening ? "Åpner..." : "Se som bruker"}
      </Button>
      {error && <span className="text-xs text-admin-danger-400">{error}</span>}
    </>
  );
}
